import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ExtendedAttributeFilterInterface } from 'app/library/all-users/interfaces/extended-attribute-filer.interface';
import { IModuleQuery } from 'app/library/module/interfaces/module.query.interface';
import { Module } from 'app/library/module/models/module.model';
import { newModule } from 'app/library/module/models/newModule';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ICourseCatalogListingQuery } from 'app/library/course-catalog-listing/interfaces/course-catalog-listing.query.interface';
import { CourseCatalogListing } from 'app/library/course-catalog-listing/models/course-catalog-listing.model';
import { ICourseOfferingExecutionsAndTotalCount } from '../interfaces/course-offering-executions-and-total-count.interface';
import { ICreateCourseOfferingExecutionActionPayload } from '../interfaces/create-course-offering-execution.action.payload.interface';
import { CourseOfferingExecutionCommand } from '../models/course-offering-execution.command.model';

@Injectable()
export class CourseOfferingExecutionsApiService {
    constructor(private http: HttpClient, @Inject(MAXBRAIN_ENVIRONMENT) private environment: IEnvironment) {}

    createCourseOfferingExecution(entity: ICreateCourseOfferingExecutionActionPayload): Observable<{ id: string; async_job_id: string }> {
        const moduleData = new CourseOfferingExecutionCommand(entity);
        return this.http.post<{ id: string; async_job_id: string }>(`${this.environment.apiUrl}/course-structure/executions`, moduleData);
    }

    getCourseOfferingExecutions(
        courseOfferingId: string,
        search = '',
        sortBy = '',
        sortOrder = '',
        pageSize = 10,
        pageIndex = 1,
        statusesIds: string[] = [],
        tags: string[] = [],
        extendedAttributesFilters: ExtendedAttributeFilterInterface[] = [],
        columns: string[] = [],
        context: string = ''
    ): Observable<ICourseOfferingExecutionsAndTotalCount> {
        let params = new HttpParams().set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (sortBy && sortOrder) {
            params = params.set('sortBy', sortBy);
            params = params.set('sortOrder', sortOrder);
        }

        if (search) {
            params = params.set('filter[query]', search);
        }

        params = this._appendExtendedAttributesFilters(params, extendedAttributesFilters);

        if (columns?.length) {
            params = params.set('filter[columns]', columns.map((column) => column).toString());
        }

        if (statusesIds?.length) {
            params = params.append('filter[status]', statusesIds.map((statusId) => statusId).toString());
        }

        if (tags?.length) {
            params = params.append('filter[tag]', tags.map((tag) => tag).toString());
        }

        if (context) {
            params = params.set('context', context);
        }

        return this.http.get<ICourseOfferingExecutionsAndTotalCount>(`${this.environment.apiUrl}/course-structure/course-offerings/${courseOfferingId}/executions`, { params });
    }

    getAllExecutions(
        search = '',
        pageSize = 10,
        pageIndex = 1,
        sortBy = '',
        sortOrder = '',
        statusesIds: string[] = [],
        tags: string[],
        types: string[],
        extendedAttributesFilters: ExtendedAttributeFilterInterface[] = [],
        executionDate: ExtendedAttributeFilterInterface = null,
        columns: string[] = [],
        context: string = ''
    ): Observable<ICourseOfferingExecutionsAndTotalCount> {
        let params = new HttpParams().set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (sortBy && sortOrder) {
            params = params.set('sortBy', sortBy);
            params = params.set('sortOrder', sortOrder);
        }

        if (search) {
            params = params.set('filter[query]', search);
        }

        params = this._appendExtendedAttributesFilters(params, extendedAttributesFilters);

        if (executionDate?.rangeValue1 && executionDate?.rangeValue2) {
            params = params.set(`filter[startDate][gte]`, executionDate.rangeValue1);
            params = params.set(`filter[endDate][lte]`, executionDate.rangeValue2);
        }

        if (columns?.length) {
            params = params.set('filter[columns]', columns.map((column) => column).toString());
        }

        if (statusesIds?.length) {
            params = params.append('filter[status]', statusesIds.map((statusId) => statusId).toString());
        }

        if (tags?.length) {
            params = params.append(`filter[tag]`, tags.map((tag) => tag).toString());
        }

        if (types?.length) {
            params = params.append(`filter[type]`, types.map((type) => type).toString());
        }

        if (context) {
            params = params.set('filter[context]', context);
        }

        return this.http.get<ICourseOfferingExecutionsAndTotalCount>(`${this.environment.apiUrl}/course-structure/executions`, { params });
    }

    findCourseOfferingExecutionsSearch(courseOfferingId: string, programExecutionId: string, tags: number[], search: string, users: string[]): Observable<Module[]> {
        let params = new HttpParams();

        params = params.set('programExecution', programExecutionId);

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (users) {
            params = params.set('users', users.toString());
        }

        if (tags.length) {
            params = params.set('filter[tag]', tags.toString());
        }

        return this.http
            .get<IModuleQuery[]>(`${this.environment.apiUrl}/course-structure/course-offerings/${courseOfferingId}/executions/search`, { params })
            .pipe(map((queries) => queries.map((query) => newModule(query))));
    }

    getProgramExecutionCourseOfferingExecutions(
        courseOfferingId = '',
        programExecutionId = '',
        scope = 1,
        search = '',
        pageSize = 10,
        pageIndex = 1,
        statusesIds: string[] = [],
        context = ''
    ): Observable<ICourseOfferingExecutionsAndTotalCount> {
        // scope=2 means: fetch all course executions, not just ones where we have program participants
        let params = new HttpParams()
            .set('page', pageIndex ? pageIndex.toString() : '1')
            .set('limit', pageSize ? pageSize.toString() : '50')
            .set('filter[scope]', scope);

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (statusesIds?.length) {
            params = params.set('filter[status]', statusesIds.join(','));
        }

        if (context) {
            params = params.set('filter[context]', context);
        }

        return this.http.get<ICourseOfferingExecutionsAndTotalCount>(
            `${this.environment.apiUrl}/course-structure/program-executions/${programExecutionId}/course-offerings/${courseOfferingId}/executions`,
            { params }
        );
    }

    getAllCourseOfferingExecutionsVersions(): Observable<{ version: string }[]> {
        return this.http.get<{ version: string }[]>(`${this.environment.apiUrl}/course-structure/executions/versions`);
    }

    getSelfPacedCourseOfferingListing(courseOfferingId: string): Observable<CourseCatalogListing> {
        return this.http
            .get<ICourseCatalogListingQuery>(`${this.environment.apiUrl}/catalog/self-paced/course-offerings/${courseOfferingId}/listing`)
            .pipe(map((response) => new CourseCatalogListing(response)));
    }

    setSelfPacedCourseOfferingListedExecution(courseOfferingId: string, executionId: string): Observable<void> {
        return this.http.put<void>(`${this.environment.apiUrl}/catalog/self-paced/course-offerings/${courseOfferingId}/listed-execution`, {
            execution_id: Number(executionId),
        });
    }

    private _appendExtendedAttributesFilters(params: HttpParams, extendedAttributesFilters: ExtendedAttributeFilterInterface[]): HttpParams {
        if (!extendedAttributesFilters?.length) {
            return params;
        }

        const attributeValues: { [key: string]: (string | number)[] } = {};

        extendedAttributesFilters.forEach((attr) => {
            if (attr.textFilters?.length) {
                const likeValues = attr.textFilters.filter((f) => f.mode === 'like').map((f) => f.value);
                const eqValues = attr.textFilters.filter((f) => f.mode === 'eq').map((f) => f.value);

                if (likeValues.length) {
                    params = params.append(`filter[${attr.attributeName}][like]`, likeValues.join(','));
                }
                if (eqValues.length) {
                    params = params.append(`filter[${attr.attributeName}][eq]`, eqValues.join(','));
                }
            } else if (attr.value) {
                if (!attributeValues[attr.attributeName]) {
                    attributeValues[attr.attributeName] = [];
                }

                attributeValues[attr.attributeName].push(attr.value);
            } else {
                params = params.set(`filter[${attr.attributeName}][gte]`, String(attr.rangeValue1));
                params = params.set(`filter[${attr.attributeName}][lte]`, String(attr.rangeValue2));
            }
        });

        Object.keys(attributeValues).forEach((key) => {
            params = params.append(`filter[${key}]`, attributeValues[key].join(','));
        });

        return params;
    }
}
